import React from 'react';
import clsx from 'clsx';

const TopNav = ({
  children,
  className,
  variant = 'default',
  onToggleSidebar,
  sidebarCollapsed = false,
  ...props
}) => {
  const variants = {
    default: 'bg-white border-b border-gray-200',
    dark: 'bg-gray-900 border-b border-gray-700',
    branded: 'bg-blue-600 border-b border-blue-500'
  };

  const navClasses = clsx(
    'flex items-center justify-between px-4 h-16',
    variants[variant],
    className
  );

  return (
    <header className={navClasses} {...props}>
      {/* Left side */}
      <div className="flex items-center space-x-4">
        {onToggleSidebar && (
          <button
            onClick={onToggleSidebar}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md lg:hidden"
            aria-label="Toggle sidebar"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        )}
        {children}
      </div>
    </header>
  );
};

const TopNavBrand = ({ 
  logo, 
  title, 
  href = '/',
  className,
  ...props 
}) => {
  const brandClasses = clsx(
    'flex items-center space-x-3 text-lg font-semibold text-gray-900',
    className
  );

  const Component = href ? 'a' : 'div';

  return (
    <Component href={href} className={brandClasses} {...props}>
      {logo && (
        <div className="flex-shrink-0">
          {typeof logo === 'string' ? (
            <img src={logo} alt={title} className="w-8 h-8" />
          ) : (
            logo
          )}
        </div>
      )}
      {title && <span className="truncate">{title}</span>}
    </Component>
  );
};

const TopNavSearch = ({ 
  placeholder = 'Search...',
  value,
  onChange,
  onSearch,
  className,
  ...props 
}) => {
  const searchClasses = clsx(
    'relative flex-1 max-w-md',
    className
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    if (onSearch) {
      onSearch(value);
    }
  };

  return (
    <form onSubmit={handleSubmit} className={searchClasses} {...props}>
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
        <input
          type="text"
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm"
        />
      </div>
    </form>
  );
};

const TopNavActions = ({ children, className, ...props }) => {
  const actionsClasses = clsx(
    'flex items-center space-x-2',
    className
  );

  return (
    <div className={actionsClasses} {...props}>
      {children}
    </div>
  );
};

const TopNavNotifications = ({
  count = 0,
  onClick,
  className,
  ...props
}) => {
  const buttonClasses = clsx(
    'relative p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md',
    className
  );

  return (
    <button onClick={onClick} className={buttonClasses} {...props}>
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-3.403-3.403A1.5 1.5 0 0116 13V6a4 4 0 10-8 0v7c0 .21-.04.408-.116.583L4.5 17H8m7 0v1a3 3 0 11-6 0v-1m6 0H9" />
      </svg>
      {count > 0 && (
        <span className="absolute top-1 right-1 inline-flex items-center justify-center px-1 py-0.5 text-xs font-bold leading-none text-white bg-red-600 rounded-full min-w-[16px] h-4">
          {count > 99 ? '99+' : count}
        </span>
      )}
    </button>
  );
};

const TopNavUser = ({
  user,
  onProfileClick,
  onSettingsClick,
  onSignOut,
  className,
  ...props
}) => {
  if (!user) return null;

  const userClasses = clsx(
    'flex items-center space-x-3 p-2 text-sm hover:bg-gray-100 rounded-md cursor-pointer',
    className
  );

  return (
    <div className={userClasses} onClick={onProfileClick} {...props}>
      <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center">
        {user.avatar ? (
          <img src={user.avatar} alt={user.name} className="w-8 h-8 rounded-full" />
        ) : (
          <span className="text-gray-600 font-medium">
            {user.name?.charAt(0)?.toUpperCase() || 'U'}
          </span>
        )}
      </div>
      <div className="hidden lg:block">
        <p className="text-sm font-medium text-gray-900">
          {user.name}
        </p>
        <p className="text-xs text-gray-500">
          {user.role || user.email}
        </p>
      </div>
      <svg className="w-4 h-4 text-gray-400 hidden lg:block" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    </div>
  );
};

const TopNavBreadcrumbs = ({ items = [], className, ...props }) => {
  if (!items.length) return null;

  const breadcrumbClasses = clsx(
    'flex items-center space-x-2 text-sm',
    className
  );

  return (
    <nav className={breadcrumbClasses} {...props}>
      {items.map((item, index) => (
        <React.Fragment key={index}>
          {index > 0 && (
            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          )}
          {item.href ? (
            <a href={item.href} className="text-gray-500 hover:text-gray-700">
              {item.label}
            </a>
          ) : (
            <span className={index === items.length - 1 ? 'text-gray-900 font-medium' : 'text-gray-500'}>
              {item.label}
            </span>
          )}
        </React.Fragment>
      ))}
    </nav>
  );
};

TopNav.Brand = TopNavBrand;
TopNav.Search = TopNavSearch;
TopNav.Actions = TopNavActions;
TopNav.Notifications = TopNavNotifications;
TopNav.User = TopNavUser;
TopNav.Breadcrumbs = TopNavBreadcrumbs;

export default TopNav;